/**
 * ListViewResponse
 *
 * Card row for a single video response with generated thumbnail, starred
 * state, and gated actions (download, delete) based on permissions.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Play, Circle, ChevronDown, Trash2, Star, Video, Download } from 'lucide-react';
import { SERVER_URL } from '../../../api/core';
import { auth } from '../../../firebase/firebase';
import { useNavigate } from 'react-router-dom';

/**
 * Helper: Convert Firestore-like timestamp objects to a JS Date.
 * Supports either { _seconds, _nanoseconds } or { seconds, nanoseconds }.
 */
function parseFirestoreTimestamp(ts) {
  if (!ts) return null;
  if (typeof ts.toDate === 'function') {
    return ts.toDate();
  }
  const seconds = ts._seconds ?? ts.seconds;
  const nanos = ts._nanoseconds ?? ts.nanoseconds ?? 0;
  if (typeof seconds === 'number' && typeof nanos === 'number') {
    return new Date(seconds * 1000 + nanos / 1000000);
  }
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Helper: Format a Date into a "time ago" string.
 */
function timeAgo(date) {
  if (!date) return 'Unknown';
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  if (diffInSeconds < 60) return 'Just now';

  const intervals = [
    { label: 'year', secs: 31536000 },
    { label: 'month', secs: 2592000 },
    { label: 'week', secs: 604800 },
    { label: 'day', secs: 86400 },
    { label: 'hour', secs: 3600 },
    { label: 'minute', secs: 60 },
  ];

  for (const interval of intervals) {
    const count = Math.floor(diffInSeconds / interval.secs);
    if (count >= 1) {
      return `${count} ${interval.label}${count > 1 ? 's' : ''} ago`;
    }
  }
  return 'Just now';
}

export function ListViewResponse({ 
  response, 
  onVideoClick, 
  onDelete, 
  onStarChange,
  userPermission,
  hasReadOnlyPermission
}) {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isStarred, setIsStarred] = useState(response.starred || false);
  const [isStarring, setIsStarring] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [error, setError] = useState(null);
  const errorTimeoutRef = useRef(null);
  const [thumbnailError, setThumbnailError] = useState(false);
  const [generatedThumbnail, setGeneratedThumbnail] = useState(null);
  const videoRef = useRef(null);
  const thumbnailLoaded = useRef(false);
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Parse the createdAt timestamp and compute the "time ago" string.
  const createdAtDate = parseFirestoreTimestamp(response.createdAt);
  const timeAgoString = createdAtDate ? timeAgo(createdAtDate) : 'Unknown';

  // Generate thumbnail from video URL (only when server didn't provide one)
  useEffect(() => {
    // If server already sent a thumbnail, do not attempt client-side extraction
    if (response.thumbnailUrl) return;
    // Only generate thumbnail if we have a videoUrl and haven't generated one yet
    if ((response.videoUrl || response.processedVideoUrl) && !thumbnailLoaded.current && !generatedThumbnail && !thumbnailError) {
      const videoUrl = response.processedVideoUrl || response.videoUrl;
      
      // Create video element to load the video and extract a frame
      const video = document.createElement('video');
      videoRef.current = video;
      
      // Avoid crossOrigin settings that can trigger CORS issues with signed URLs
      video.muted = true;
      video.playsInline = true;
      
      // Set up event listeners for extracting thumbnail
      video.onloadeddata = () => {
        try {
          // Seek to 0.5 seconds (or 25% of the duration if very short)
          const seekTime = Math.min(video.duration * 0.25, 0.5);
          video.currentTime = seekTime;
        } catch (err) {
          console.error('Error seeking video:', err);
          setThumbnailError(true);
        }
      };
      
      video.onseeked = () => {
        try {
          // Create a canvas to draw the video frame
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          
          // Draw the video frame to the canvas
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Convert canvas to data URL
          const thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
          setGeneratedThumbnail(thumbnailUrl);
          thumbnailLoaded.current = true;
          
          // Clean up
          video.pause();
          URL.revokeObjectURL(video.src);
          videoRef.current = null;
        } catch (err) {
          console.error('Error generating thumbnail:', err);
          setThumbnailError(true);
        }
      };
      
      video.onerror = () => {
        console.error('Error loading video for thumbnail');
        setThumbnailError(true);
        URL.revokeObjectURL(video.src);
        videoRef.current = null;
      };
      
      // Start loading the video
      video.src = videoUrl;
      video.load();
    }
    
    // Clean up function
    return () => {
      if (videoRef.current) {
        videoRef.current.pause();
        URL.revokeObjectURL(videoRef.current.src);
        videoRef.current = null;
      }
    };
  }, [response.thumbnailUrl, response.videoUrl, response.processedVideoUrl, generatedThumbnail, thumbnailError]);

  useEffect(() => {
    if (error) {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
      errorTimeoutRef.current = setTimeout(() => setError(null), 3000);
    }
    return () => {
      if (errorTimeoutRef.current) clearTimeout(errorTimeoutRef.current);
    };
  }, [error]);

  const handleStar = async (e) => {
    e.stopPropagation();
    
    // Prevent action for read-only users
    if (hasReadOnlyPermission) {
      setError("You don't have permission to star responses");
      return;
    }
    
    if (isStarring) return;

    if (!response.id) {
      console.error('No video ID provided');
      setError('No video ID provided. Please try again.');
      return;
    }
    setIsStarring(true);
    setError(null);
    const newStarredState = !isStarred;
    try {
      const idToken = await auth.currentUser.getIdToken();
      const res = await fetch(`${SERVER_URL}/videoEditor/update-metadata/${response.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ starred: newStarredState })
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update star status');
      }
      await res.json();
      setIsStarred(newStarredState);
      onStarChange(response.id, newStarredState);
    } catch (err) {
      console.error('Error updating star status:', err);
      setIsStarred(!newStarredState);
      setError(err.message || 'Failed to update star status. Please try again.');
    } finally {
      setIsStarring(false);
    }
  };

  const handleCardClick = (e) => {
    // Do not toggle expand if clicking video area or an action button.
    if (e.target.closest('.video-area') || e.target.closest('.action-button')) return;
    setIsExpanded(!isExpanded);
  };

  // Prefer server-provided thumbnail; fallback to client-generated
  const thumbnailSrc = response.thumbnailUrl || generatedThumbnail || null;

  const handleThumbnailError = () => {
    setThumbnailError(true);
  };

  const handleDownload = async (e) => {
    e.stopPropagation();
    
    // If already downloading, prevent multiple clicks
    if (isDownloading) return;
    
    // Get the video URL from the response
    const videoUrl = response.processedVideoUrl || response.videoUrl;
    
    if (!videoUrl) {
      setError('Video URL not available for download');
      return;
    }
    
    try {
      // Set downloading state to true
      setIsDownloading(true);
      setError(null);
      
      // Create a formatted name for the download
      let fileName = 'video_advocacyLab.mp4';
      
      // Use person's name if available
      if (response.firstName && response.lastName) {
        fileName = `${response.firstName}_${response.lastName}_advocacyLab.mp4`;
      } else if (response.name) {
        fileName = `${response.name}_advocacyLab.mp4`;
      }
      
      // Create an XMLHttpRequest to fetch the video as a blob
      const xhr = new XMLHttpRequest();
      xhr.open('GET', videoUrl, true);
      xhr.responseType = 'blob';
      
      xhr.onload = function() {
        if (this.status === 200) {
          // Create a blob URL from the response
          const blob = new Blob([this.response], { type: 'video/mp4' });
          const url = URL.createObjectURL(blob);
          
          // Create a temporary link element to trigger the download
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          
          // Clean up
          setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            setIsDownloading(false);
          }, 100);
        } else {
          throw new Error('Failed to download video');
        }
      };
      
      xhr.onerror = function() {
        throw new Error('Network error occurred while downloading');
      };
      
      // Set a timeout to show a success message if the download takes too long
      const timeoutId = setTimeout(() => {
        if (isDownloading) {
          setError('Download started. The file should appear in your downloads folder shortly.');
        }
      }, 3000);
      
      // Start the download
      xhr.send();
      
      // Clean up timeout if component unmounts
      return () => clearTimeout(timeoutId);
    } catch (err) {
      console.error('Error downloading video:', err);
      setError('Failed to download video. Please try again.');
      setIsDownloading(false);
    }
  };



  return (
    <div
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group relative flex flex-col rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 transition-all duration-300 ease-in-out overflow-hidden cursor-pointer hover:border-gray-300 hover:shadow-lg hover:scale-[1.01] hover:bg-gray-50/50 dark:hover:border-gray-700 dark:hover:bg-gray-800/50 h-full"
    >
      <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:p-4">
        {/* Thumbnail section */}
        <div
          onClick={(e) => {
            e.stopPropagation();
            onVideoClick(response);
          }}
          className="video-area relative aspect-video w-full lg:w-48 overflow-hidden lg:rounded-lg hover:opacity-90 transition-opacity"
        >
          {response.isNew && (
            <div className="absolute top-2 right-2 z-10 flex items-center gap-1.5 rounded-full bg-primary-500 px-2 py-1 text-xs font-medium text-white">
              <Circle className="h-2 w-2 fill-current" />
              <span>New</span>
            </div>
          )}
          
          {thumbnailSrc && !thumbnailError ? (
            <>
              <img
                src={thumbnailSrc}
                alt={response.firstName && response.lastName 
                  ? `${response.firstName} ${response.lastName}'s video`
                  : 'Video response'}
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                onError={handleThumbnailError}
              />
              <div className="absolute inset-0 bg-black/40 transition-opacity group-hover:bg-black/50" />
            </>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 dark:from-gray-900 dark:to-black flex items-center justify-center">
              <Video className="h-12 w-12 text-gray-400 dark:text-gray-600 opacity-50" />
            </div>
          )}
          
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="rounded-full bg-white/10 p-3 backdrop-blur-sm transition-all duration-200 group-hover:scale-110 group-hover:bg-white/20">
              <Play className="h-6 w-6 text-white" />
            </div>
          </div>
        </div>

        {/* Info area */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 lg:p-0 flex-1">
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 dark:text-white truncate">
              {response.firstName && response.lastName 
                ? `${response.firstName} ${response.lastName}`
                : response.name || 'Anonymous'}
            </h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 truncate">
              {response.email || 'No email provided'}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
              {response.zipCode ? (
                <div className="flex items-center gap-2">
                  <span className="shrink-0 font-medium text-gray-500 dark:text-gray-500 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">
                    Zip Code
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">{response.zipCode}</span>
                </div>
              ) : null}
              
              <div className="flex items-center gap-2">
                <span className="shrink-0 font-medium text-gray-500 dark:text-gray-500 px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded">
                  Uploaded
                </span>
                <span className="text-gray-600 dark:text-gray-400">{timeAgoString}</span>
              </div>
            </div>
          </div>

          {/* Star and expand icons */}
          <div className="flex items-center gap-2 self-end sm:self-auto">
            <button
              onClick={handleStar}
              disabled={isStarring || !!error || hasReadOnlyPermission}
              title={hasReadOnlyPermission ? "You don't have permission to star responses" : (isStarred ? "Unstar this response" : "Star this response")}
              className={`action-button p-2 rounded-lg transition-all ${
                isStarring || hasReadOnlyPermission ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105'
              } ${
                isStarred
                  ? 'text-purple-500 dark:text-purple-400'
                  : 'text-gray-500 hover:text-purple-500 dark:text-gray-400 dark:hover:text-purple-400'
              }`}
            >
              <Star className={`w-5 h-5 ${isStarred ? 'fill-current' : ''}`} />
            </button>
            
            <ChevronDown
              className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''} group-hover:text-gray-700 dark:group-hover:text-gray-300`}
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="px-4 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border-t border-red-100 dark:border-red-900/50">
          {error}
        </div>
      )}

      {/* Expandable Content - always visible when expanded but buttons disabled for read-only users */}
      <div className={`overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-[280px] md:max-h-[320px]' : 'max-h-0'}`}>
        <div className="border-t border-gray-200 dark:border-gray-800 p-4">
          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
            <button
              className={`action-button inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium w-full sm:w-auto justify-center transition-all ${
                hasReadOnlyPermission 
                  ? 'bg-primary-400 cursor-not-allowed dark:bg-primary-800/50' 
                  : isDownloading
                    ? 'bg-primary-500 text-white'
                    : 'bg-primary-600 text-white hover:bg-primary-700 hover:scale-105'
              }`}
              onClick={hasReadOnlyPermission ? (e) => {
                e.stopPropagation();
                setError("You don't have permission to download videos");
                } : handleDownload
              }
              disabled={hasReadOnlyPermission || isDownloading}
              title={
                hasReadOnlyPermission 
                  ? "You don't have permission to download videos" 
                  : isDownloading 
                    ? "Download in progress..." 
                    : "Download video"
              }
            >
              {isDownloading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent"></div>
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Download Video
                </>
              )}
            </button>
            

            
            <button
              className={`action-button inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium w-full sm:w-auto justify-center ${
                hasReadOnlyPermission 
                  ? 'border-gray-200 bg-white/80 text-red-300 cursor-not-allowed dark:border-gray-800/50 dark:bg-gray-900/50 dark:text-red-500/50' 
                  : 'border-gray-200 bg-white text-red-600 hover:bg-gray-50 hover:scale-105 transition-transform dark:border-gray-800 dark:bg-gray-900 dark:text-red-400 dark:hover:bg-gray-800'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (hasReadOnlyPermission) {
                  setError("You don't have permission to delete responses");
                  return;
                }
                onDelete(response);
              }}
              disabled={hasReadOnlyPermission}
              title={hasReadOnlyPermission ? "You don't have permission to delete responses" : "Delete response"}
            >
              <Trash2 className="w-4 h-4" />
              Delete Response
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
